import { FC, ReactNode, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { Typography } from '../Typography';
import { ChevronUp } from '../svgs';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface GameKeyPanelProps {
  /**
   * Content rendered inside the expanded panel.
   * Typically a scoring table or QuickGuideContent slice.
   */
  children?: ReactNode;

  /**
   * Uncontrolled initial expanded state.
   * @default false
   */
  defaultExpanded?: boolean;

  /**
   * Controlled expanded state.
   * When provided the component does not manage open/closed itself.
   */
  expanded?: boolean;

  /** Fired when the header is clicked. Receives the new desired expanded state. */
  onExpandedChange?: (expanded: boolean) => void;

  className?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * GameKeyPanel — collapsible "GAME KEY" card.
 *
 * Renders as a white pill card with a shadow matching the modal shell.
 * The header row always shows "GAME KEY" + a rotating chevron toggle.
 * Expanded content is rendered below a divider and accepts any children.
 *
 * Supports both controlled and uncontrolled usage.
 *
 * @example
 * // Uncontrolled
 * <GameKeyPanel>
 *   <QuickGuideContent topTab="points" subTab="all-players" />
 * </GameKeyPanel>
 *
 * @example
 * // Controlled
 * <GameKeyPanel expanded={open} onExpandedChange={setOpen}>
 *   <p>Scoring rules here...</p>
 * </GameKeyPanel>
 */
export const GameKeyPanel: FC<GameKeyPanelProps> = ({
  children,
  defaultExpanded = false,
  expanded: controlledExpanded,
  onExpandedChange,
  className,
}) => {
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);

  // Prefer controlled value when provided
  const isExpanded = controlledExpanded ?? internalExpanded;

  const toggle = () => {
    const next = !isExpanded;
    setInternalExpanded(next);
    onExpandedChange?.(next);
  };

  return (
    <div
      className={twMerge(
        'w-full bg-white rounded-[12px]',
        'shadow-[0px_4px_40px_0px_rgba(34,37,38,0.2)]',
        className
      )}
    >
      {/* ── Header (always visible) ──────────────────────────────────────── */}
      <button
        type="button"
        onClick={toggle}
        aria-expanded={isExpanded}
        className={twMerge(
          'flex w-full items-center gap-4 px-4 py-2 min-h-[48px]',
          'hover:opacity-80 transition-opacity',
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#030033]',
          // Round all corners when collapsed; only top corners when expanded
          isExpanded ? 'rounded-t-[12px]' : 'rounded-[12px]'
        )}
      >
        <Typography
          as="span"
          variant="label050"
          className="flex-1 text-left text-[#222526] !font-[800]"
        >
          GAME KEY
        </Typography>

        {/* Chevron: points up when expanded, down when collapsed */}
        <ChevronUp
          className={twMerge(
            'size-6 shrink-0 text-[#222526] transition-transform duration-200',
            isExpanded ? 'rotate-0' : 'rotate-180'
          )}
          aria-hidden
        />
      </button>

      {/* ── Expanded content ─────────────────────────────────────────────── */}
      {isExpanded && children && (
        <div className="border-t border-[#eae9fd] rounded-b-[12px] overflow-hidden">{children}</div>
      )}
    </div>
  );
};
